import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Descriptions, Typography, Space } from 'antd';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ProposalService from '../services/ProposalService';
import PhoneNumberDisplay from './PhoneNumberDisplay';

const { Text } = Typography;

const DEFAULT_LAT = 41.0082;
const DEFAULT_LNG = 28.9784;

const defaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const formatAddressPart = (value?: string | null): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLocaleUpperCase('tr-TR') : '-';
};

type ProposalLike = {
  id?: number;
  proposalNumber?: string;
  company?: { id?: number; name?: string | null } | null;
  address?: {
    title?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    state?: string | null;
    city?: string | null;
    phone?: string | null;
    sgkNumber?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
};

interface ControlAddressCardProps {
  proposalId?: number | null;
  proposal?: ProposalLike | null;
  title?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onProposalLoaded?: (proposal: ProposalLike | null) => void;
  fetchOnMount?: boolean;
  loading?: boolean;
  onBack?: () => void;
  extraActions?: React.ReactNode;
}

const InspectorControlAddressCard: React.FC<ControlAddressCardProps> = ({
  proposalId,
  proposal: proposalProp,
  title = 'Kontrol Adresi',
  className,
  style,
  onProposalLoaded,
  fetchOnMount = true,
  loading: loadingProp,
  onBack,
  extraActions,
}) => {
  const navigate = useNavigate();
  const [internalLoading, setInternalLoading] = useState<boolean>(false);
  const [proposal, setProposal] = useState<ProposalLike | null>(proposalProp ?? null);
  const numericProposalId = useMemo(() => {
    if (proposalId == null) return null;
    const num = Number(proposalId);
    return Number.isFinite(num) && num > 0 ? num : null;
  }, [proposalId]);
  const isLoading = loadingProp ?? internalLoading;

  useEffect(() => {
    setProposal(proposalProp ?? null);
  }, [proposalProp]);

  useEffect(() => {
    if (!fetchOnMount) {
      return;
    }
    if (!numericProposalId) {
      return;
    }
    if (proposalProp && Number(proposalProp.id) === numericProposalId) {
      return;
    }

    let ignore = false;
    const load = async () => {
      try {
        setInternalLoading(true);
        const response = await ProposalService.get(numericProposalId);
        if (ignore) {
          return;
        }
        const data = (response as any)?.data ?? response ?? null;
        setProposal(data);
        onProposalLoaded?.(data ?? null);
      } catch (error) {
        if (!ignore) {
          console.error('ControlAddressCard: failed to load proposal', error);
          setProposal(null);
          onProposalLoaded?.(null);
        }
      } finally {
        if (!ignore) {
          setInternalLoading(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [numericProposalId, proposalProp, onProposalLoaded, fetchOnMount]);

  const address = proposal?.address ?? null;
  const latitude = typeof address?.latitude === 'number' ? address.latitude : null;
  const longitude = typeof address?.longitude === 'number' ? address.longitude : null;
  const mapCenter = useMemo<[number, number]>(() => {
    if (latitude != null && longitude != null) {
      return [latitude, longitude];
    }
    return [DEFAULT_LAT, DEFAULT_LNG];
  }, [latitude, longitude]);

  return (
    <Card
      title={title}
      className={className}
      style={style}
      loading={isLoading}
      extra={
        <Space size={8}>
          {extraActions}
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBack ? onBack : () => navigate(-1)}
            style={{ width: 'fit-content' }}
          >
            Geri
          </Button>
        </Space>
      }
    >
      {!isLoading && proposal ? (
        <>
          <Descriptions
            column={1}
            bordered
            size='small'
          >
            {proposal?.company?.id && proposal?.company?.name ? (
              <Descriptions.Item
                label='Firma'
                labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
              >
              
                  {proposal.company.name}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item
              label='Adres Başlığı'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              {latitude != null && longitude != null && address?.title ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  title="Google Haritalar'da aç"
                  style={{ color: '#1677ff', cursor: 'pointer' }}
                >
                  {address.title}
                </a>
              ) : (
                <span style={{ color: 'rgba(0, 0, 0, 0.88)' }}>{address?.title ?? '-'}</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label='Adres'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              <span style={{ color: 'rgba(0, 0, 0, 0.88)' }}>
                {address?.addressLine1 ?? '-'}
                {address?.addressLine2 ? ` ${address.addressLine2}` : ''}
              </span>
            </Descriptions.Item>
            <Descriptions.Item
              label='İlçe / İl'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              {`${formatAddressPart(address?.state)} / ${formatAddressPart(address?.city)}`}
            </Descriptions.Item>
            <Descriptions.Item
              label='Telefon'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              {address?.phone ? <PhoneNumberDisplay value={address.phone} /> : '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label='SGK/DETSİS Numarası'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              {address?.sgkNumber ?? '-'}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
            <MapContainer
              center={mapCenter}
              zoom={latitude != null && longitude != null ? 12 : 8}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
              {latitude != null && longitude != null ? (
                <Marker position={[latitude, longitude]} icon={defaultIcon} />
              ) : null}
            </MapContainer>
          </div>
        </>
      ) : !isLoading && numericProposalId ? (
        <Text>Adres bilgisi bulunamadı.</Text>
      ) : !isLoading ? (
        <Text>Geçerli bir teklif seçilmedi.</Text>
      ) : null}
    </Card>
  );
};

export default InspectorControlAddressCard;
